import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AppDialog from "components/common/AppDialog";
import CustomButton from "components/common/CustomButton";
import RunStatusChip from "components/integrations-beta/RunStatusChip";
import TimezoneDisplayToggle from "components/integrations-beta/TimezoneDisplayToggle";
import {
  IntegrationDetail,
  useUpdateIntegrationMutation,
} from "features/integrationsBeta/integrationsBeta";
import {
  frequencyItemsForZone,
  resolveDisplayZone,
  type TimezoneDisplayMode,
} from "features/integrationsBeta/timezoneDisplay";
import { usePlatformRole } from "features/integrationsBeta/usePlatformRole";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useState } from "react";
import { formatIsoDateTime, zoneAbbreviation } from "utils/dateUtils";

interface Props {
  integration: IntegrationDetail;
  statusLabel: (value: string | null | undefined) => string;
  /** Display zone for Frequency / Last Run / Next Run and the History tab. */
  timezoneMode: TimezoneDisplayMode;
  onTimezoneModeChange: (mode: TimezoneDisplayMode) => void;
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ mt: 0.25 }}>{children}</Box>
    </Grid>
  );
}

function TypeChips({ items }: { items: string[] }) {
  if (!items.length) {
    return <Typography variant="body1">—</Typography>;
  }
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {items.map((t) => (
        <Chip key={t} label={t} size="small" variant="outlined" />
      ))}
    </Stack>
  );
}

export default function IntegrationDetailsHeader({
  integration,
  statusLabel,
  timezoneMode,
  onTimezoneModeChange,
}: Props) {
  const dispatch = useAppDispatch();
  const { isAdmin, isSuperuser } = usePlatformRole();
  const [editOpen, setEditOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState(integration.name);
  const [infoOpen, setInfoOpen] = useState(false);
  const [descDraft, setDescDraft] = useState(integration.description);
  const [updateIntegration, { isLoading: isSaving }] =
    useUpdateIntegrationMutation();

  const tz = integration.timezone;
  const tzLabel = integration.timezone_abbreviation
    ? `${tz} | ${integration.timezone_abbreviation}`
    : tz;
  const lastRun = integration.last_run;
  const displayZone = resolveDisplayZone(timezoneMode, tz);
  const frequency = frequencyItemsForZone(
    integration.frequency,
    integration.schedules
      .filter((s) => s.is_active)
      .map((s) => s.cron_expression),
    tz,
    timezoneMode,
  );
  // Grey zone marker next to values whenever they are NOT in the toggle's
  // obvious zone (client mode, or a frequency that could not be shifted).
  const clientAbbr = zoneAbbreviation(new Date().toISOString(), tz);
  const markFrequency =
    frequency.items.length > 0 &&
    (timezoneMode === "client" || !frequency.inSelectedZone);
  const markTimes = timezoneMode === "client";

  const zoneMark = (visible: boolean, abbr: string) =>
    visible ? (
      <Typography variant="caption" color="text.secondary">
        {abbr}
      </Typography>
    ) : null;

  const openEdit = () => {
    setNameDraft(integration.name);
    setEditOpen(true);
  };

  const openInfo = () => {
    setDescDraft(integration.description);
    setInfoOpen(true);
  };

  const handleSaveDescription = async () => {
    try {
      await updateIntegration({
        id: integration.id,
        description: descDraft,
      }).unwrap();
      dispatch(
        pushNotification({
          severity: "success",
          message: "Description updated.",
        }),
      );
      setInfoOpen(false);
    } catch {
      dispatch(
        pushNotification({
          severity: "error",
          message: "Could not update the description.",
        }),
      );
    }
  };

  const handleSave = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) return;
    try {
      await updateIntegration({ id: integration.id, name: trimmed }).unwrap();
      dispatch(
        pushNotification({
          severity: "success",
          message: "Integration name updated.",
        }),
      );
      setEditOpen(false);
    } catch {
      dispatch(
        pushNotification({
          severity: "error",
          message: "Could not update the integration name.",
        }),
      );
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 1,
        boxShadow: 2,
        mb: 2,
        flexShrink: 0,
        backgroundColor: "background.paper",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
        <Grid container spacing={2} sx={{ flex: 1, minWidth: 0 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Client
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {integration.client_name}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Integration
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body1" fontWeight={500}>
                {integration.name}
              </Typography>
              {isAdmin && (
                <Tooltip title="Rename integration">
                  <IconButton
                    size="small"
                    onClick={openEdit}
                    aria-label="Edit integration name"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Integration info">
                <IconButton
                  size="small"
                  onClick={openInfo}
                  aria-label="Integration info"
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
        <Tooltip title="Timezone the times below and in History are shown in">
          <Box sx={{ flexShrink: 0 }}>
            <TimezoneDisplayToggle
              mode={timezoneMode}
              onChange={onTimezoneModeChange}
            />
          </Box>
        </Tooltip>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Grid container spacing={2}>
        <Field label="Source Types">
          <TypeChips items={integration.source_services} />
        </Field>
        <Field label="Target Types">
          <TypeChips items={integration.target_services} />
        </Field>
        <Field label="Timezone">
          <TypeChips items={[tzLabel]} />
        </Field>
        <Field label="Frequency">
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <TypeChips items={frequency.items} />
            {zoneMark(markFrequency, clientAbbr)}
          </Stack>
        </Field>
        <Field label="Last Run">
          {lastRun ? (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Chip
                size="small"
                variant="outlined"
                label={formatIsoDateTime(lastRun.started_at, displayZone)}
              />
              {zoneMark(
                markTimes,
                zoneAbbreviation(lastRun.started_at, displayZone),
              )}
              <RunStatusChip
                status={lastRun.status}
                label={statusLabel(lastRun.status)}
              />
            </Stack>
          ) : (
            <Typography variant="body1">—</Typography>
          )}
        </Field>
        <Field label="Next Run">
          {integration.next_run_at ? (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <TypeChips
                items={[
                  formatIsoDateTime(integration.next_run_at, displayZone),
                ]}
              />
              {zoneMark(
                markTimes,
                zoneAbbreviation(integration.next_run_at, displayZone),
              )}
            </Stack>
          ) : (
            <Typography variant="body1">—</Typography>
          )}
        </Field>
      </Grid>

      <AppDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Rename Integration"
        maxWidth="sm"
        showCloseIcon
        actions={
          <>
            <CustomButton fullWidth={false} onClick={() => setEditOpen(false)}>
              Cancel
            </CustomButton>
            <CustomButton
              fullWidth={false}
              onClick={handleSave}
              disabled={isSaving || !nameDraft.trim()}
            >
              Save
            </CustomButton>
          </>
        }
      >
        <TextField
          autoFocus
          fullWidth
          label="Integration name"
          value={nameDraft}
          onChange={(e) => setNameDraft(e.target.value)}
          sx={{ mt: 1 }}
        />
      </AppDialog>

      <AppDialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        title="Integration Info"
        maxWidth="sm"
        showCloseIcon
        actions={
          isSuperuser ? (
            <>
              <CustomButton
                fullWidth={false}
                onClick={() => setInfoOpen(false)}
              >
                Cancel
              </CustomButton>
              <CustomButton
                fullWidth={false}
                onClick={handleSaveDescription}
                disabled={isSaving}
              >
                Save
              </CustomButton>
            </>
          ) : (
            <CustomButton fullWidth={false} onClick={() => setInfoOpen(false)}>
              Close
            </CustomButton>
          )
        }
      >
        {isSuperuser ? (
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            label="Description"
            value={descDraft}
            onChange={(e) => setDescDraft(e.target.value)}
            sx={{ mt: 1 }}
          />
        ) : (
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", mt: 1 }}>
            {integration.description || "No description provided."}
          </Typography>
        )}
      </AppDialog>
    </Box>
  );
}
